import React from "react";
import { SbBlokData, storyblokEditable } from "@storyblok/react";
import {
  BlogAuthor,
  BlogContent,
  BlogDetails,
  HeroBlogPost,
} from "@/.storyblok/types/319751/storyblok-components";
import Image from "next/image";
import Link from "next/link";

interface HeroBlogPostProps {
  blok?: HeroBlogPost & SbBlokData;
  blog_details?: BlogDetails[] & SbBlokData;
  blog_content?: BlogContent & SbBlokData;
  blog_author?: BlogAuthor[] & SbBlokData;
}

const HeroBlogPostComponent: React.FC<HeroBlogPostProps> = ({
  blok,
  blog_details = [],
  blog_content,
  blog_author = [],
}) => {
  if (!blok) return null;

  const { title, subtitle, description, image, blog_type } =
    blok;

  const firstBlogDetail =
    Array.isArray(blog_details) && blog_details.length > 0
      ? blog_details[0]
      : null;
  const topics = firstBlogDetail?.topics || "General";
  const blogDate = firstBlogDetail?.date
    ? new Date(firstBlogDetail.date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    })
    : null;

  const firstAuthor =
    Array.isArray(blog_author) && blog_author.length > 0
      ? blog_author[0]
      : null;

  const h1Content = (
    <>
      {title && (
        <>
          <span>{title}<span>&nbsp;</span></span>
        </>
      )}
      {subtitle && <span>{subtitle}</span>}
    </>
  );

  const renderImage = (image: HeroBlogPost["image"] | undefined) => {
    return image?.filename || "";
  };

  const calcReadTime = (blogContent: BlogContent[]): number => {
    if (!Array.isArray(blogContent) || blogContent.length === 0) {
      return 0;
    }

    const extractText = (nodes: any[]): string => {
      return nodes
        .map((node) => {
          if (node.type === "text" && node.text) {
            return node.text;
          }
          if (Array.isArray(node.content)) {
            return extractText(node.content);
          }
          return "";
        })
        .join(" ");
    };

    const allText = blogContent
      .map((item) => extractText(item?.content?.content || []))
      .join(" ");

    const wordCount = allText.split(/\s+/).length;
    const wordsPerMinute = 225;
    return Math.ceil(wordCount / wordsPerMinute);
  };

  const readingTime = calcReadTime(
    Array.isArray(blog_content)
      ? blog_content
      : blog_content
        ? [blog_content]
        : []
  );

  if (blog_type === "legacy") {
    return (
      <div
        {...storyblokEditable(blok)}
        className="blog-header"
        style={{backgroundImage: `url(${renderImage(image)})`}}
      >
        <div className="container">
          <div className="blog-header-inner">
            <h1 style={{ textRendering: 'optimizeSpeed' }}>{title}</h1>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...storyblokEditable(blok)} className="page-hero scroll-trigger on">
      <div className="container">
        <div className="row">
          <div className="col-24 col-lg-16 page-hero-content pt-4 pb-6 py-lg-6 py-xl-7">
            <span className="blog-preview-tag">{topics}</span>

            <h1 style={{ textRendering: 'optimizeSpeed' }}>{h1Content}</h1>
            {description && (
              <p className="page-hero-subtitle mb-4">{description}</p>
            )}

            {firstAuthor && (
              <div className="page-header-author mt-4 mb-lg-n6 mb-xl-n7 py-3 py-xl-4">
                <div className="d-flex flex-column flex-md-row align-items-center">
                  {(firstAuthor?.content as BlogAuthor)?.image?.filename && (
                    <Image
                      src={(firstAuthor.content as BlogAuthor).image!.filename || ""}
                      width={36}
                      height={36}
                      alt={firstAuthor.name || "Author"}
                      className="rounded-circle avatar mb-2 mb-md-0"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      loading="eager"
                      quality={75}
                    />
                  )}
                  <div className="ms-3">
                    <p className="h5 text-secondary me-2 mb-2 mb-md-0">
                      <Link
                        href="#author-bio"
                        className="page-header-author-link text-decoration-none text-white"
                      >
                        <i className="fa fa-user me-1"></i> {firstAuthor.name}
                      </Link>
                      {(firstAuthor?.content as BlogAuthor)?.title && (
                        <span className="d-block d-md-inline blog-author-title text-light">
                          - {(firstAuthor.content as BlogAuthor).title}
                        </span>
                      )}
                    </p>
                  </div>

                  {blogDate && (
                    <div className="blog-author-meta ms-md-auto">
                      <span className="text-light me-2">
                        <i className="fas fa-calendar me-2"></i> {blogDate}
                      </span>
                    </div>
                  )}

                  {readingTime > 0 && (
                    <span className="text-light ms-md-3">
                      <i className="fa fa-clock me-2"></i> {readingTime} min
                      read
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {image && image.filename && (
        <picture>
          <Image
            src={renderImage(image)}
            alt={image.alt || "HeroBlogPost image"}
            className="page-hero-img scroll-trigger fade-in duration-2s img-blur-bg on"
            width={1680}
            height={945}
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
            loading="eager"
            quality={75}
          />
        </picture>
      )}
    </div>
  );
};

export default HeroBlogPostComponent;